import type { Styles } from '@adornis/ass/style.js';
import { injectAttributesOf } from '@adornis/chemistry/directives/inject-attributes.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-formfield-wrapper.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { inputToken, inputResetStyle as realInputResetStyle } from '@adornis/chemistry/elements/theming/input-token.js';
import { xComponents } from '@adornis/chemistry/elements/x-components.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { Price } from '@adornis/payments/db/prices.js';
import { html, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { ref } from 'lit/directives/ref.js';
import { fromEvent, takeUntil } from 'rxjs';

/**
 * Currency Input
 *
 * Takes Price object as input and displays it as euro/dollar
 *
 * ```ts
 *   html`
 *     <x-currency-input placeholder="Steve was here"
 *       @value-changed=${(e: ValueEvent<string>) => console.log(e.detail.value)}></x-currency-input>
 *   `
 * ```
 *
 * ---
 *
 * Extends `FormField`
 *
 * **Possible attributes:**
 *
 * - `min : number` => if type is `number`, this determines the minimum number selectable, the input will correct the value up if something smaller is typed or pasted in.
 * - `max : number` => if type is `number`, this determines the maximum number selectable, the input will correct the value down if something bigger is typed or pasted in.
 * - `placeholder : string` => Placeholder to be rendered if the value is empty
 * - `placeholder-mode : 'floating' | 'inline-floating' | 'prefix' | 'hide'` => how the placeholder is rendered, default `floating`
 * - `bold : boolean` => whether the input should be bold
 *
 * **Possible state:**
 *
 * - `blockClick : boolean` => Whether any clicks on this element should be stopped propagating, default `true`
 *
 * **Possible events:**
 *
 * - `value-picked : ValueEvent<string | number>` => fires the current value if the input blurs
 * - `hit-max : CustomEvent<void>` => if type is `number`, fires if the user uses the arrow keys to input a value above the set `max` value
 * - `hit-min : CustomEvent<void>` => if type is `number`, fires if the user uses the arrow keys to input a value below the set `min` value
 * - `submit : ValueEvent<string | number>` => fires the current value if the user hits enter on the input
 * - `escape : CustomEvent<void>` => fired if the user hits escape on the input
 *
 * @element x-currency-input
 */
@customElement('tr-currency-input')
export class TrCurrencyInput extends FormField<Price> {
  get inputToken() {
    return inputToken(this);
  }

  get currency() {
    return this.value.value?.currency;
  }

  get postfix() {
    if (!this.currency) return '';
    if (this.currency === 'EUR') return '€';
    throw new Error(`Currency ${this.currency} is not supported`);
  }

  get useCommaInsteadOfDot() {
    if (this.currency === 'EUR') return true;
    return false;
  }

  override styles() {
    return [
      realInputResetStyle(this),
      {
        ':host': {
          height: 'unset',
          fontSize: this.baseFontSize,
          cursor: 'text',
        },
      },
    ] as Styles[];
  }

  @property({ type: Number, attribute: 'default' }) defaultValue?: number;
  @property({ type: Boolean, attribute: true }) bold = false;
  @property({ type: Boolean, attribute: 'disable-blur-submit' }) disableBlurSubmit?: boolean;
  @state() focused = false;

  protected _input?: HTMLInputElement;

  protected get _showLabel() {
    return (this.value.value !== undefined && this.value.value !== null) || !!this.error;
  }

  override connectedCallback() {
    super.connectedCallback();

    // click on padding should also focus
    fromEvent(this, 'click')
      .pipe(takeUntil(this.disconnected))
      .subscribe(() => this._input?.focus());
  }

  override render() {
    return xComponents(html`
      <x-formfield-wrapper
        one-line
        ?show-label=${this._showLabel}
        postfix=${this.postfix}
        ${injectAttributesOf(this, ['icon', 'error'])}
        ?clearable=${false}
        error=${ifDefined(this.error)}
      >
        <x-text flex ?bold=${this.bold}> ${this.input()} </x-text>
      </x-formfield-wrapper>
    `) as TemplateResult<1>;
  }

  private input() {
    return html`
      <input
        @focus=${() => (this.focused = true)}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            const oldValue = this.value.value?.unitAmount ?? 0;
            this.updateUnitamount(oldValue + 1);
          }
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            const oldValue = this.value.value?.unitAmount ?? 0;
            this.updateUnitamount(oldValue - 1);
          }
        }}
        @keyup=${(e: KeyboardEvent) => {
          if (e.key === 'Enter')
            return this.dispatchEvent(
              new CustomEvent('submit', {
                detail: { value: this.processNewValue() },
                composed: true,
                bubbles: true,
              }),
            );
          if (e.key === 'Escape') return this.dispatchEvent(new CustomEvent('escape'));
        }}
        @blur=${(e: Event) => {
          // This can avoid unneded re-rendering in cases where value-changed is used
          // and the user would interact outside the input after typing
          if (this.disableBlurSubmit) {
            return;
          }

          this.focused = false;
          this.processNewValue();
          this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: this.value.value } }));
        }}
        @click=${(e: Event) => e.stopPropagation()}
        .value=${live(this.displayValue())}
        ?disabled=${this.disabled}
        type="text"
        inputmode="decimal"
        ${injectAttributesOf(this, ['type', 'disabled'])}
        ${ref(input => (this._input = input as HTMLInputElement | undefined))}
      />
    `;
  }

  private processNewValue() {
    if (!this._input) return;
    const value = this._input.value;

    const parsedValue = (() => {
      if (value === '') return null; // dont set a number if nothing is entered
      // different characters for dots: https://www.compart.com/en/unicode/U+002E
      const dots = [0x0000002e, 0x00002024, 0x0000fe52, 0x0000ff0e].map(charCode => String.fromCharCode(charCode));
      let rawString = value.replace(',', '.'); // no commas
      dots.forEach(dot => (rawString = rawString.replace(dot, '.'))); // no special snowflake dots
      const rawNumber = +rawString; // need a number
      if (Number.isNaN(rawNumber)) return null;
      return rawNumber * 100;
    })();

    this.updateUnitamount(parsedValue);
  }

  private updateUnitamount(newValue: number | null | undefined) {
    newValue = newValue ?? this.defaultValue;
    if (typeof newValue !== 'number') return this.value.next(null);

    // apply step size
    newValue = Math.round(newValue);

    const min = Math.max(0, +(this.getAttribute('min') || -Infinity));
    const max = +(this.getAttribute('max') || Infinity);
    if (newValue > max) {
      this.dispatchEvent(new CustomEvent('hit-max'));
      newValue = max;
    }
    if (newValue < min) {
      this.dispatchEvent(new CustomEvent('hit-min'));
      newValue = min;
    }

    const price = this.value.value ?? new Price({});
    price.unitAmount = newValue;

    this.value.next(price);
    return price;
  }

  private displayValue() {
    const price = this.value.value;
    if (price === undefined || price === null) return '';
    const eur = price.unitAmount / 100;
    const str = eur.toFixed(2);
    if (this.useCommaInsteadOfDot) return str.replace('.', ',');
    return str;
  }
}
